import { ConfigType, Env } from "@recare/core/types";
import { fromJS } from "immutable";

export const ENV_STAGING = "staging";
export const ENV_PREPROD = "preprod";
export const ENV_PRODUCTION = "production";
export const ENV_DEVELOPMENT = "development";
export const ENV_DEMO = "demo";
export const hasWindow = typeof window !== "undefined";
export const isNative = hasWindow && !window.navigator.userAgent;
export const isFrance =
  isNative || (hasWindow && window.location?.hostname?.endsWith(".fr"));

const base = {
  backend: {
    protocol: "https",
    port: 443,
  },
  algolia: isFrance
    ? {
        appId: "25ZFFO4C0C",
        apiKey: "e980465e0ba771feff9fa46ef90c152f",
      }
    : {
        appId: "19R893IBVD",
        apiKey: "916100f98c0ea60a1641e2235ffe962a",
      },
  algoliaPlaces: {
    appId: "plUMR85GPG5L",
    apiKey: "4ec46277c39ea2d6750909c17db05499",
  },
};

const faqUrl = {
  DE: "https://help.recaresolutions.com/",
  FR: "https://help.recaresolutions.fr/",
};

// export for test
export const tierConf: Record<Env, Partial<ConfigType>> = {
  production: {
    mapbox:
      "pk.eyJ1IjoiY2hhcmxlc2NvdGUiLCJhIjoiY2pwaThxdTN4MGhvazN2cXcxcHg1d2h0ZSJ9.Mux3R3v_1oBJ5Uc2Den4NQ",
    backend: {
      host: isFrance ? "api.recaresolutions.fr" : "api.recaresolutions.com",
    },
    webview: isFrance
      ? "https://partner.recaresolutions.fr/careprovider/native_bridge"
      : "https://partner.recaresolutions.com/careprovider/native_bridge",
    intercom: {
      key: "s7bhk3oq",
      faqUrl,
    },
    seald: isFrance
      ? {
          appId: "067366c2-49e8-4a36-97d2-728e0c6555cb",
          apiUrl: "https://api.seald.io/",
          ssksUrl: "https://ssks.seald.io/",
        }
      : {
          appId: "14caf043-6062-4465-9bc3-55fed8aee42c",
          apiUrl: "https://api.seald.io/",
          ssksUrl: "https://ssks.seald.io/",
        },
  },
  demo: {
    mapbox:
      "pk.eyJ1IjoiY2hhcmxlc2NvdGUiLCJhIjoiY2pwaThxdTN4MGhvazN2cXcxcHg1d2h0ZSJ9.Mux3R3v_1oBJ5Uc2Den4NQ",
    backend: {
      host: "api-preprod.recaresolutions.com",
    },
    webview:
      "https://partner-demo.recaresolutions.com/careprovider/native_bridge",
    intercom: {
      key: "arwx3wb7",
      faqUrl,
    },
    seald: {
      appId: "9bbc6d92-5464-49ea-886b-b8b52ad08ff2",
      apiUrl: "https://api.seald.io/",
      ssksUrl: "https://ssks.seald.io/",
    },
  },
  preprod: {
    mapbox:
      "pk.eyJ1IjoiY2hhcmxlc2NvdGUiLCJhIjoiY2pwaThxdTN4MGhvazN2cXcxcHg1d2h0ZSJ9.Mux3R3v_1oBJ5Uc2Den4NQ",
    backend: {
      host: "api-preprod.recaresolutions.com",
    },
    webview:
      "https://partner-preprod.recaresolutions.com/careprovider/native_bridge",
    intercom: {
      faqUrl,
      key: "arwx3wb7",
    },
    seald: {
      appId: "9bbc6d92-5464-49ea-886b-b8b52ad08ff2",
      apiUrl: "https://api.seald.io/",
      ssksUrl: "https://ssks.seald.io/",
    },
  },
  staging: {
    mapbox:
      "pk.eyJ1IjoibGl2ZXRlc3QiLCJhIjoiY2p6Nnl1dzVtMDZ2ZTNtbnlldmhsc2w0eCJ9.ziQLuzK7AJIQyhAJqWm_yA",
    backend: {
      host: process.env.PR_NAME
        ? `${process.env.PR_NAME}-backendpr.staging.recaresolutions.com`
        : "api-staging.recaresolutions.com",
      url: process.env.PR_NAME
        ? `https://${process.env.PR_NAME}-backendpr.staging.recaresolutions.com`
        : "https://api-staging.recaresolutions.com",
    },
    webview: process.env.PR_NAME
      ? `https://${process.env.PR_NAME}-pr.staging.recaresolutions.com/careprovider/native_bridge`
      : "https://partner-staging.recaresolutions.com/careprovider/native_bridge",
    intercom: {
      key: "arwx3wb7",
      faqUrl,
    },
    seald: {
      appId: "b0739385-c414-41a0-a23b-72a89554e984",
      apiUrl: "https://api.staging-0.seald.io/",
      ssksUrl: "https://ssks.staging-0.seald.io/",
    },
  },
  development: {
    mapbox:
      "pk.eyJ1IjoibGl2ZXRlc3QiLCJhIjoiY2p6Nnl1dzVtMDZ2ZTNtbnlldmhsc2w0eCJ9.ziQLuzK7AJIQyhAJqWm_yA",
    backend: {
      host: "api-staging.recaresolutions.com",
    },
    webview: "http://localhost:9090/careprovider/native_bridge",
    intercom: {
      key: "arwx3wb7",
      faqUrl,
    },
    seald: {
      appId: "b0739385-c414-41a0-a23b-72a89554e984",
      apiUrl: "https://api.staging-0.seald.io/",
      ssksUrl: "https://ssks.staging-0.seald.io/",
    },
  },
};

const envs: Array<Env> = [
  "development",
  "staging",
  "demo",
  "preprod",
  "production",
];

function overrideBackendHost(tierConfig: Partial<ConfigType>) {
  if (window.localStorage?.getItem) {
    let backendHostOverride;
    try {
      backendHostOverride = window.localStorage.getItem(
        "backendConfigOverride",
      );
    } catch (err) {
      console.error(err, `overrideBackendHost: ${backendHostOverride}`);
    }
    if (backendHostOverride) {
      tierConfig.backend = { host: backendHostOverride };
    }
  }
  return tierConfig;
}

export function getConfig(environment?: Env) {
  if (!environment) {
    console.error("Missing env, defaulting to development");
    environment = "development";
  }

  const isProduction = env === ENV_PRODUCTION;
  let tierConfig = tierConf[environment];

  if (!isNative && !isProduction) {
    tierConfig = overrideBackendHost(tierConfig);
  }

  const topLevelDomain = isFrance ? "fr" : "com";

  return fromJS(base)
    .mergeDeep(fromJS(tierConfig))
    .mergeDeep({
      environment,
      version: process.env.BUILD_VERSION,
      domain: `recaresolutions.${topLevelDomain}`,
    })
    .toJS();
}

function getEnvironmentFromLocalStorage() {
  let environment: Env = "staging";
  if (window.localStorage?.getItem) {
    try {
      environment =
        (window.localStorage.getItem("env") as Env) ||
        process.env.ENV ||
        "staging";
    } catch (err) {
      console.error(
        err,
        `getBackendConfig environment: ${JSON.stringify(environment)}`,
      );
    }
  }
  return environment;
}

export function getBackendConfig() {
  const environment = getEnvironmentFromLocalStorage();
  return getConfig(environment).backend;
}

const env = (process.env.ENV as Env) || (global as unknown as { ENV: Env }).ENV;

if (env == null) {
  console.warn(`process.env.ENV/global.ENV is not defined`);
} else if (envs.find((e) => env == e) === undefined) {
  console.warn(
    `process.env.ENV/global.ENV isn't a valid env : ${
      process.env.ENV || ""
    }, valids are ${envs.join(" / ")}`,
  );
}

export function getStaticAsset(assetPath: string): string {
  return `https://assets.recaresolutions.com/recare/${assetPath}`;
}

const Config: ConfigType = getConfig(env);

export default Config;
export const BackendConfig = Config.backend;

let app: number | null = null;

export const getApp = () => app;

export function setApp(appName: number) {
  app = appName;
}
